import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { Error } from '../design/elements';
import FormField from './formField';
import { LocationWrapper } from './css';
import {
  fieldTelusOptikTvNumberWiredSetTopBoxes,
  fieldTelusOptikTvNumberWirelessSetTopBoxes,
  fieldTelusOptikTvAdditionalSetTopBox
} from '../utils/constant';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import { telusOptikTvPricing } from '../utils/rates';

const OptikRepeaterForm = ({
  data,
  formErrors,
  formFields,
  orderData,
  setFormFields,
  isEditing,
  isFormTouched,
  setIsFormTouched,
  dueNow,
  dueMonthly,
  setDueNow,
  setDueMonthly
}) => {
  const { key, formFields: contentFulFields } = data;
  const localizedStrings = useLocalizedStringsContext();
  const {
    modalForm: { fieldRequiredMessage }
  } = localizedStrings;
  let formsList = formFields[key];
  formsList =
    formsList && (formsList.length ? formsList : Object.values(formsList));
  const repeatedFormError = formErrors[key];
  const [isInitial, setIsInitial] = useState(true);
  const { setTopBoxPricing } = telusOptikTvPricing;

  const initialPricing = {
    dueNow: 0,
    dueMonthly: 0
  };

  const formObjectKeys = useMemo(
    () =>
      contentFulFields.reduce((acc, { fields: { name: formName, value } }) => {
        acc[formName] = value;
        return acc;
      }, {}),
    [contentFulFields]
  );

  const fieldsMap = useMemo(
    () =>
      contentFulFields.reduce((acc, { fields }) => {
        acc[fields.name] = { ...fields };
        return acc;
      }, {}),
    [contentFulFields]
  );

  const getTotalPrice = fields => {
    const price = fields.reduce(
      (acc, item) => {
        const fieldValue = item[fieldTelusOptikTvAdditionalSetTopBox];
        acc.dueNow += (setTopBoxPricing[fieldValue] || {}).price || 0;
        acc.dueMonthly += (setTopBoxPricing[fieldValue] || {}).monthly || 0;
        return acc;
      },
      { ...initialPricing }
    );

    return { ...price };
  };

  const calculatePricing = (oldValues, newValues) => {
    const oldPrice = getTotalPrice(oldValues);
    const newPrice = getTotalPrice(newValues);
    setDueNow(dueNow - oldPrice.dueNow + newPrice.dueNow);
    setDueMonthly(dueMonthly - oldPrice.dueMonthly + newPrice.dueMonthly);
  };

  const addNewForm = () => {
    const updatedFormFields = { ...formFields };
    updatedFormFields[key] = [...formsList, { ...formObjectKeys }];
    setFormFields(updatedFormFields);
  };

  const removeFormGroup = index => {
    const oldValues = _.cloneDeep(formsList);
    const fieldList = formsList;
    fieldList.splice(index, 1);
    const updatedFormFields = { ...formFields };
    updatedFormFields[key] = formsList;
    setFormFields(updatedFormFields);
    calculatePricing(oldValues, fieldList);
  };

  useEffect(() => {
    if (isEditing && orderData && orderData.id && orderData[key]) {
      const updatedFormFields = { ...formFields };
      updatedFormFields[key] = [...orderData[key]];
      setFormFields(updatedFormFields);
      if (isFormTouched) {
        setIsFormTouched(false);
      }
      if (isInitial) setIsInitial(false);
    } else if (orderData && orderData.id && !orderData[key]) {
      if (isInitial) setIsInitial(false);
    }
  }, [isEditing, orderData]);

  useEffect(() => {
    if (
      formFields[fieldTelusOptikTvNumberWiredSetTopBoxes] ||
      formFields[fieldTelusOptikTvNumberWirelessSetTopBoxes]
    ) {
      const numberOfBoxes =
        (+formFields[fieldTelusOptikTvNumberWiredSetTopBoxes].value || 0) +
        (+formFields[fieldTelusOptikTvNumberWirelessSetTopBoxes].value || 0) -
        1;
      if (numberOfBoxes > formsList.length && !isInitial) {
        addNewForm();
      } else if (numberOfBoxes < formsList.length && formsList.length > 0) {
        removeFormGroup(formsList.length - 1);
      }
    }
  }, [formFields]);

  const handleFormField = (index, fieldKey, value) => {
    const oldValues = _.cloneDeep(formsList);
    const fieldList = formsList;
    fieldList[index][fieldKey] = value;
    const updatedFormFields = { ...formFields };
    updatedFormFields[key] = fieldList;
    if (!isFormTouched) {
      setIsFormTouched(true);
    }
    setFormFields(updatedFormFields);
    calculatePricing(oldValues, fieldList);
  };

  return (
    <LocationWrapper>
      {formsList && formsList.map((form, index) => {
        return (
          <>
            {Object.keys(form).map(formKey => {
              const fieldKey = `repeatedForm${key}${formKey}${index}`;
              const currentValue = formsList[index][formKey];
              const fieldLabel = `${fieldsMap[formKey].label} ${index + 1}`;
              const fieldData = {
                ...fieldsMap[formKey],
                name: `${formKey}-${index}`,
                sysId: fieldKey,
                value: currentValue,
                label: fieldLabel,
                onChange: ({ target: { value } }) =>
                  handleFormField(index, formKey, value)
              };
              const error =
                repeatedFormError &&
                  !currentValue &&
                  fieldsMap[formKey].required
                  ? fieldRequiredMessage
                  : '';
              return (
                <Fragment key={fieldKey}>
                  <FormField
                    key={`field${formKey}${fieldKey}`}
                    data={fieldData}
                  />
                  {error && <Error text={error} />}
                </Fragment>
              );
            })}
          </>
        );
      })}
    </LocationWrapper>
  );
};

OptikRepeaterForm.propTypes = {
  data: PropTypes.shape({
    key: PropTypes.string.isRequired,
    formFields: PropTypes.arrayOf(PropTypes.object),
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired
  }).isRequired,
  formErrors: PropTypes.objectOf(PropTypes.object),
  formFields: PropTypes.objectOf(PropTypes.object).isRequired,
  orderData: PropTypes.objectOf(PropTypes.object).isRequired,
  setFormFields: PropTypes.func.isRequired,
  isEditing: PropTypes.string.isRequired,
  isFormTouched: PropTypes.bool.isRequired,
  setIsFormTouched: PropTypes.func.isRequired,
  dueNow: PropTypes.number.isRequired,
  setDueNow: PropTypes.func.isRequired,
  dueMonthly: PropTypes.number.isRequired,
  setDueMonthly: PropTypes.func.isRequired
};

OptikRepeaterForm.defaultProps = {
  formErrors: {}
};

export default OptikRepeaterForm;
